import { goBack } from "connected-react-router";
import { isEqual } from "lodash";
import { batch, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useMutation, useRequest } from "redux-query-react";
import { CashflowCustomModelFromJSON, usePostCashflowCustomModel, usePutCashflowCustomModel, deleteCashflowCustomModel, getCashflowCustomModelById, usePutModelAssociation } from "api/admin/src";
import { is2xxStatusCode } from "common/number-helper";
import { MessagesDialogFunctionName } from "components/frame/helper";
import { MinDate } from "components/pages/administration/cashflow-forecast/insDeprecatedAsOfDate";
import { language } from "language/default";
import { resetStateToDefault, setBypassUnsavedChangesPrompt, setValidationMessage } from "store/administration/cashflow-forecasting/model-editing";
import { resetChanges } from "store/administration/cashflow-forecasting/ta-model-editing";
import { setMessagesDialog } from "store/messages-dialog";
const transform = (body) => {
    // eslint-disable-next-line new-cap
    const objResponse = CashflowCustomModelFromJSON(body);
    return { CashflowCustomModel: { response: objResponse },
        CashflowCustomModelVersions: { response: [objResponse] },
        CashflowCustomModels: { response: objResponse },
        CashflowFile: { id: objResponse?.id } };
};
const update = {
    CashflowCustomModel: (prev, transformedValue) => {
        if (transformedValue?.response?.id) {
            return { ...prev, [transformedValue.response.id]: transformedValue.response };
        }
        return prev;
    },
    CashflowCustomModelVersions: (prev, transformedValue) => {
        if (transformedValue?.response?.[0].id && transformedValue?.response?.[0].modelVersion !== 1) {
            if (!prev[transformedValue.response[0].id]) {
                return { ...prev,
                    [transformedValue.response[0].id]: [
                        {
                            asOfDate: MinDate,
                            createdBy: transformedValue?.response?.[0].createdBy,
                            description: transformedValue?.response?.[0].description,
                            id: transformedValue?.response?.[0].id,
                            lastUpdated: transformedValue?.response?.[0].lastUpdated,
                            modelVersion: transformedValue?.response?.[0].modelVersion,
                            name: transformedValue?.response?.[0].name,
                            type: transformedValue?.response?.[0].type
                        }
                    ] };
            }
            return { ...prev,
                [transformedValue.response[0].id]: [
                    ...prev[transformedValue.response[0].id],
                    {
                        asOfDate: MinDate,
                        createdBy: transformedValue?.response?.[0].createdBy,
                        description: transformedValue?.response?.[0].description,
                        id: transformedValue?.response?.[0].id,
                        lastUpdated: transformedValue?.response?.[0].lastUpdated,
                        modelVersion: transformedValue?.response?.[0].modelVersion,
                        name: transformedValue?.response?.[0].name,
                        type: transformedValue?.response?.[0].type
                    }
                ] };
        }
        return prev;
    },
    CashflowCustomModels: (prev, transformedValue) => {
        if (transformedValue?.response?.id) {
            return { ...prev, [transformedValue.response.id]: transformedValue.response };
        }
        return prev;
    },
    CashflowFile: (prev, response) => {
        if (response?.id) {
            const updated = { ...prev };
            delete updated[response.id];
            return updated;
        }
        return prev;
    }
};
const updateForDelete = (id) => ({
    CashflowCustomModel: (prevValue) => {
        const updateValue = { ...prevValue };
        delete updateValue[id];
        return updateValue;
    },
    CashflowCustomModelVersions: (prevValue) => {
        const updateValue = { ...prevValue };
        delete updateValue[id];
        return updateValue;
    },
    CashflowCustomModels: (prevValue) => {
        const updateValue = { ...prevValue };
        delete updateValue[id];
        return updateValue;
    }
});
const optimisticUpdateForDelete = (id) => ({
    CashflowCustomModel: (prevValue) => {
        const optimisticValue = { ...prevValue };
        delete optimisticValue[id];
        return optimisticValue;
    },
    CashflowCustomModelVersions: (prevValue) => {
        const optimisticValue = { ...prevValue };
        delete optimisticValue[id];
        return optimisticValue;
    },
    CashflowCustomModels: (prevValue) => {
        const optimisticValue = { ...prevValue };
        delete optimisticValue[id];
        return optimisticValue;
    }
});
const rollbackForDelete = (id) => ({
    CashflowCustomModel: (initialValue, currentValue) => {
        const rollbackValue = { ...currentValue };
        rollbackValue[id] = initialValue[id];
        return rollbackValue;
    },
    CashflowCustomModelVersions: (initialValue, currentValue) => {
        const rollbackValue = { ...currentValue };
        rollbackValue[id] = initialValue[id];
        return rollbackValue;
    },
    CashflowCustomModels: (initialValue, currentValue) => {
        const rollbackValue = { ...currentValue };
        rollbackValue[id] = initialValue[id];
        return rollbackValue;
    }
});
export const useCustomGetCashFlowCustomModelById = () => {
    const { id } = useParams();
    const config = id ? getCashflowCustomModelById({ id }) : null;
    const [queryState, refresh] = useRequest(config);
    const cashflowCustomModel = useSelector((state) => state.entities.CashflowCustomModel[id || ""]);
    return { cashflowCustomModel, queryState, refresh };
};
export const getPostCashflowModelQueryKey = () => "AddCustomCashflowModel";
export const getPutCustomModelQueryKey = (id) => `UpdateCustomCashflowModel-${id}`;
export const useCustomPostCashflowCustomModel = (id, sharedState = true) => {
    const queryKey = getPostCashflowModelQueryKey();
    const dispatch = useDispatch();
    const [nonFixedQueryState, mutation] = usePostCashflowCustomModel({ queryKey, transform, update });
    const fixedQueryState = useSelector((state) => state.customQueries[queryKey], isEqual);
    const errors = useSelector((state) => state.requestErrors[queryKey]?.responseBody, isEqual);
    const queryState = sharedState ? fixedQueryState : nonFixedQueryState;
    const mut = ((model, queryParam) => mutation(model, queryParam)?.then((value) => {
        if (is2xxStatusCode(value?.status)) {
            batch(() => {
                dispatch(resetChanges({ id: id || "" }));
                dispatch(setBypassUnsavedChangesPrompt({ bypassUnsavedChangesPrompt: true }));
                dispatch(goBack());
            });
        }
        else {
            const responseBodyErrors = value.body;
            batch(() => {
                const errorMessages = Object.values(responseBodyErrors.errors || {}).flat();
                dispatch(setValidationMessage({ validationMessage: errorMessages }));
                if (errorMessages.length > 0) {
                    dispatch(setMessagesDialog({
                        functionName: MessagesDialogFunctionName.Default,
                        messages: errorMessages.map((m) => Object({ message: m })),
                        title: language.en.administration.marketDataAdministration.indices.importValidationErrors.dialogTitle,
                        type: "alert"
                    }));
                }
            });
        }
    }));
    return [queryState, mut, errors];
};
export const useCustomPutCashflowCustomModel = (id, sharedState = true) => {
    const queryKey = getPutCustomModelQueryKey(id);
    const dispatch = useDispatch();
    const [nonFixedQueryState, mutation] = usePutCashflowCustomModel({ queryKey, transform, update });
    const fixedQueryState = useSelector((state) => state.customQueries[queryKey], isEqual);
    const errors = useSelector((state) => state.requestErrors[queryKey]?.responseBody, isEqual);
    const queryState = sharedState ? fixedQueryState : nonFixedQueryState;
    const mut = ((updateId, model, queryParam) => mutation(updateId, model, queryParam)?.then((value) => {
        if (is2xxStatusCode(value?.status)) {
            batch(() => {
                dispatch(resetChanges({ id: id || "" }));
                dispatch(resetStateToDefault());
            });
        }
        else {
            const responseBodyErrors = value.body;
            const errorMessages = Object.values(responseBodyErrors.errors || {}).flat();
            batch(() => {
                dispatch(setValidationMessage({ validationMessage: errorMessages }));
                if (errorMessages.length > 0) {
                    dispatch(setMessagesDialog({
                        functionName: MessagesDialogFunctionName.Default,
                        messages: errorMessages.map((m) => Object({ message: m })),
                        title: language.en.administration.marketDataAdministration.indices.importValidationErrors.dialogTitle,
                        type: "alert"
                    }));
                }
            });
        }
    }));
    return [queryState, mut, errors];
};
export const useCustomDeleteCashflowCustomModel = () => useMutation((id, customQueryParam) => {
    const customQueryConfig = { optimisticUpdate: optimisticUpdateForDelete(id),
        rollback: rollbackForDelete(id),
        update: updateForDelete(id) };
    return deleteCashflowCustomModel({ id }, { ...customQueryConfig, meta: { ...customQueryConfig?.meta, ...customQueryParam } });
});
export const useUpdateModelAssociation = () => {
    const [fallbackState, mutate] = usePutModelAssociation({ queryKey: "PutModelAssociation" });
    const state = useSelector((s) => s.customQueries.PutModelAssociation, isEqual);
    return [state ?? fallbackState, mutate];
};
